// Detects the embedded webviews that social apps use instead of a real browser.
//
// This matters because Google refuses OAuth inside an embedded webview and returns
// 403 disallowed_useragent — enforced on Google's side, for every site, with nothing
// we can configure to change it. Offering "Sign Up with Google" in one of these is
// offering a button that cannot work.
//
// Detection is by explicit app marker only. The tempting heuristic — an iOS UA with
// "Mobile/" but no "Safari/" — catches more webviews but also false-positives on
// legitimate browsers, and hiding social sign-up from someone who could have used it
// costs more than the case it catches.

export interface InAppBrowser {
  /** Human-readable app name, for telling the visitor what to do. */
  app: string;
  /** Android can hand off to Chrome directly; iOS cannot be escaped in script. */
  isAndroid: boolean;
}

const MARKERS: Array<[RegExp, string]> = [
  [/Instagram/i, 'Instagram'],
  [/FBAN|FBAV|FB_IAB/i, 'Facebook'],
  [/\bLine\//i, 'LINE'],
  [/TikTok|musical_ly/i, 'TikTok'],
  [/Snapchat/i, 'Snapchat'],
  [/Pinterest/i, 'Pinterest'],
  [/LinkedInApp/i, 'LinkedIn'],
];

/** Every app name detectInAppBrowser can return. acquisition.ts accepts only these from a URL (#937). */
export const IN_APP_NAMES: readonly string[] = MARKERS.map(([, app]) => app);

export function detectInAppBrowser(
  ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
): InAppBrowser | null {
  if (!ua) return null;
  for (const [pattern, app] of MARKERS) {
    if (pattern.test(ua)) return { app, isAndroid: /Android/i.test(ua) };
  }
  return null;
}

/**
 * An Android intent URL that opens the given https URL in Chrome, leaving the
 * webview behind. Android resolves this natively, so there is no scripted probing and
 * no way to open two browsers by accident.
 */
export function androidChromeUrl(url: string): string {
  const stripped = url.replace(/^https?:\/\//, '');
  return `intent://${stripped}#Intent;scheme=https;package=com.android.chrome;end`;
}

/**
 * Meta's own escape hatch on iOS: `instagram://extbrowser/` hands the URL to whichever
 * browser the device treats as default, from inside Instagram or Threads.
 *
 * Undocumented, and Meta owes us no compatibility, so this is offered as ONE step with
 * the menu instructions still shown beside it rather than in place of it. If the scheme
 * stops resolving the visitor loses a button, not the only way out.
 *
 * Two things this deliberately does not do:
 *
 *  - It is never assigned to window.location. Meta's webview silently drops a scripted
 *    navigation to this scheme; the documented-by-observation behaviour is that only a
 *    declarative navigation is honoured. A real anchor the visitor taps is the closest
 *    declarative form available to us, and it is the safest: nothing fires unless they
 *    choose it.
 *  - It never fires automatically on load. There is no success signal, so an automatic
 *    attempt followed by a manual one is how people end up with two browsers open,
 *    which is worse than not escaping at all. It also hijacks everyone who was content
 *    to use the email form.
 *
 * Returns null where we have no scheme we trust, which is every app but Instagram on
 * iOS. x-safari-https:// is reported to work for TikTok, Reddit and X, but is also
 * reported to be actively blocked inside Meta's apps, and Instagram is effectively all
 * of this traffic, so it is not worth shipping on one source.
 */
export function iosExternalBrowserUrl(app: string, url: string): string | null {
  if (app !== 'Instagram') return null;
  return `instagram://extbrowser/?url=${encodeURIComponent(url)}`;
}
